import {
    OneVerifyFlagType,
    QuotaUsageType,
} from "@common-shared/config/oneVerify/oneVerifyCoverage.config"
import {
    COMMON_ONE_VERIFY_POLICIES_CONFIG,
    OneVerifyOverrideCommentOperation,
    PolicyDenialRisk,
} from "@common-shared/config/oneVerify/oneVerifyPolicies.config"

export const COMMON_ONE_VERIFY_GENERAL_CONFIG = {
    frontAppDomain: "https://coverage.meetnirvana.com",
    frontRegisterPath: "/auth/register",
    frontLoginPath: "/auth/login",
    frontDownloadReportPath: "/download/exports",

    // Once we change filters in UI and it's a breaking change -> we need to update this version too
    // So old filters that will break UI won't be returned
    // For old filters then we can go through them and update to work with new structure or just drop
    policiesListFiltersCurrentVersion: 1,

    eagleEye: {
        headers: {
            userId: "nirvana-eagle-eye-user-id",
            practiceId: "nirvana-eagle-eye-practice-id",
        },

        // Routes that are not get and allowed
        allowedActionRoutes: [
            "/v1/one-verify/practices/:practiceId/configurations",
            "/v1/one-verify/practices/:practiceId/payer-mappings",
            "/v1/one-verify/practices/:practiceId/payer-mappings/bulk",
            "/v1/one-verify/practices/:practiceId/payer-mappings/bulk/validate",
        ] as string[],
    },

    // How many credits used for each type of check
    get quotaUsageAmounts(): Record<QuotaUsageType, number> {
        return {
            [QuotaUsageType.GENERAL]: 1,
            [QuotaUsageType.DISCOVER]: 2,
            [QuotaUsageType.SMART_SCAN]: 4,
            [QuotaUsageType.MEDICAID]: 2, // for now
            [QuotaUsageType.AGENTIC_PORTAL]: 1, // for now
            [QuotaUsageType.AGENTIC_PHONE]: 8, // for now
        }
    },

    flags: {
        get medicaidFlagTypes(): OneVerifyFlagType[] {
            return [
                OneVerifyFlagType.POLICY_NOT_FOUND,
                OneVerifyFlagType.INACTIVE_POLICY,
                OneVerifyFlagType.ADDITIONAL_POLICY_DETECTED,
                OneVerifyFlagType.REVIEW_REQUIRED,
                OneVerifyFlagType.DEMOGRAPHICS_MISMATCH,
                OneVerifyFlagType.UNKNOWN_COSTSHARE,
                OneVerifyFlagType.UNKNOWN_ACCUMULATOR,
                OneVerifyFlagType.UNKNOWN_MEMBER_OBLIGATION,
                OneVerifyFlagType.CREDENTIALING_ERROR,
                OneVerifyFlagType.ENROLLMENT_REQUIRED,
                OneVerifyFlagType.MEMBER_ID_MISMATCH,
                OneVerifyFlagType.MEMBER_DOB_MISMATCH,
                OneVerifyFlagType.MANAGED_MEDICAID,
                OneVerifyFlagType.QUALIFIED_MEDICARE_BENEFICIARY,
            ]
        },

        policyExpiresSoonDiffDays: 30,

        get flagsSeverity(): Record<OneVerifyFlagType, number> {
            return {
                // High 70+ including
                [OneVerifyFlagType.INACTIVE_POLICY]: 100,
                [OneVerifyFlagType.POLICY_NOT_FOUND]: 90,
                [OneVerifyFlagType.VISITS_MET]: 80,
                [OneVerifyFlagType.REVIEW_REQUIRED]: 75,
                [OneVerifyFlagType.MEMBER_ID_MISMATCH]: 74,
                [OneVerifyFlagType.MEMBER_DOB_MISMATCH]: 73,
                [OneVerifyFlagType.CREDENTIALING_ERROR]: 72,
                [OneVerifyFlagType.ENROLLMENT_REQUIRED]: 71,
                [OneVerifyFlagType.THIRD_PARTY_PAYER_DETECTED]: 70,
                // Medium 40(including) - 70(excluding)
                [OneVerifyFlagType.EXPIRING_SOON]: 60,
                [OneVerifyFlagType.UNKNOWN_VISITS]: 55,
                [OneVerifyFlagType.DEMOGRAPHICS_MISMATCH]: 50,
                [OneVerifyFlagType.MEDICARE_ADVANTAGE]: 49,
                [OneVerifyFlagType.MANAGED_MEDICAID]: 48,
                [OneVerifyFlagType.ADDITIONAL_POLICY_DETECTED]: 41,
                // Low less than 40
                [OneVerifyFlagType.QUALIFIED_MEDICARE_BENEFICIARY]: 39,
                [OneVerifyFlagType.UNKNOWN_COSTSHARE]: 38,
                [OneVerifyFlagType.UNKNOWN_ACCUMULATOR]: 35,
                [OneVerifyFlagType.UNKNOWN_MEMBER_OBLIGATION]: 31,
                [OneVerifyFlagType.TELEHEALTH_COST_SHARE_DIFFERENCES]: 30,
                [OneVerifyFlagType.OOP_MAX_MET]: 29,
                [OneVerifyFlagType.DEDUCTIBLE_MET]: 25,
                [OneVerifyFlagType.NEW_PATIENT]: 10,
                // Hide from alerts overview
                [OneVerifyFlagType.NETWORK_STATUS_MISMATCH]: -1,
            }
        },

        get flagsByDenialRisk(): Record<
            PolicyDenialRisk,
            Array<OneVerifyFlagType>
        > {
            // Typescript can't type 'this' so call the object itself
            const flagsSeverity =
                COMMON_ONE_VERIFY_GENERAL_CONFIG.flags.flagsSeverity

            return {
                [PolicyDenialRisk.HIGH]: Object.keys(flagsSeverity).filter(
                    key =>
                        flagsSeverity[key as OneVerifyFlagType] >=
                        COMMON_ONE_VERIFY_POLICIES_CONFIG.highAttentionFlagSeverityCheck,
                ) as OneVerifyFlagType[],

                [PolicyDenialRisk.MEDIUM]: Object.keys(flagsSeverity).filter(
                    key =>
                        flagsSeverity[key as OneVerifyFlagType] >=
                            COMMON_ONE_VERIFY_POLICIES_CONFIG.mediumAttentionFlagSeverityCheck &&
                        flagsSeverity[key as OneVerifyFlagType] <
                            COMMON_ONE_VERIFY_POLICIES_CONFIG.highAttentionFlagSeverityCheck,
                ) as OneVerifyFlagType[],

                [PolicyDenialRisk.LOW]: Object.keys(flagsSeverity).filter(
                    key =>
                        flagsSeverity[key as OneVerifyFlagType] <
                        COMMON_ONE_VERIFY_POLICIES_CONFIG.mediumAttentionFlagSeverityCheck,
                ) as OneVerifyFlagType[],

                [PolicyDenialRisk.NONE]: [],
            }
        },
    },

    overrides: {
        get overrideCommentOperationMapping(): Record<
            OneVerifyOverrideCommentOperation,
            string
        > {
            return {
                [OneVerifyOverrideCommentOperation.CREATE]: "Override created",
                [OneVerifyOverrideCommentOperation.UPDATE]: "Override updated",
                [OneVerifyOverrideCommentOperation.DELETE]: "Override removed",
            }
        },
        // Currently not an active email address, used as creator for override project tasks
        billerOverrideProjectCreatorEmail: "overrides@meetnirvana.com",
        billerOverrideProjectCreatorFirstName: "Override",
        billerOverrideProjectCreatorLastName: "Automation",
    },
}
